import { useState } from 'react'
import { Link } from 'react-router-dom'
import { calculateVehicleScore, getScoreTier, getNextTierActions, getTierProgress, getScoreBreakdownSummary } from '../lib/garageScore'
import { Camera, FileText, Wrench, Shield, TrendingUp, Award, Gift, ChevronDown, ChevronUp, CheckCircle, Star, ArrowRight } from 'lucide-react'

const TIER_REWARDS = {
  'Barn Find': { next: 'Daily Driver', reward: 'Unlock: Basic concierge access' },
  'Daily Driver': { next: 'Show Ready', reward: 'Unlock: Priority concierge scheduling' },
  'Show Ready': { next: 'Concours', reward: 'Unlock: 10% concierge credit + featured in community' },
  'Concours': { next: 'Best in Show', reward: 'Unlock: 15% concierge credit + premium provenance badge' },
  'Best in Show': { next: null, reward: 'Maximum rewards: 15% concierge credit, premium badge, featured status' },
}

const CATEGORY_ICONS = {
  Photos: Camera,
  VIN: Shield,
  'Service Records': Wrench,
  Documents: FileText,
  Valuation: TrendingUp,
  Details: Star,
  'Maintenance Schedule': Wrench,
}

const ScoreCircle = ({ score, tier }) => {
  const radius = 34
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
        <circle
          cx="40" cy="40" r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          className="text-rdc-stone/20"
        />
        <circle
          cx="40" cy="40" r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={tier.textClass}
          style={{ transition: 'stroke-dashoffset 0.5s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-lg font-bold ${tier.textClass}`}>{score}%</span>
      </div>
    </div>
  )
}

const CategoryBar = ({ name, earned, maxPoints, percent }) => {
  const Icon = CATEGORY_ICONS[name] || Star
  const isComplete = earned === maxPoints

  return (
    <div className="flex items-center gap-3">
      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-green-100 text-green-600' : 'bg-rdc-cream text-rdc-taupe'}`}>
        {isComplete ? <CheckCircle size={14} /> : <Icon size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs font-medium text-rdc-charcoal">{name}</span>
          <span className="text-xs text-rdc-taupe">{earned}/{maxPoints}</span>
        </div>
        <div className="w-full bg-rdc-cream rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-rdc-orange'}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

export default function VehicleCompleteness({ vehicle, serviceRecordCount, documentCount, valuationCount, photoCount, vehicleId, isOwner }) {
  const [showBreakdown, setShowBreakdown] = useState(false)

  const score = calculateVehicleScore(vehicle, serviceRecordCount, documentCount, valuationCount, photoCount)
  const tier = getScoreTier(score.score)
  const progress = getTierProgress(score.score)
  const breakdown = getScoreBreakdownSummary(score.breakdown)
  const actions = isOwner ? getNextTierActions(vehicle, serviceRecordCount, documentCount, valuationCount, photoCount, vehicleId) : []
  const tierReward = TIER_REWARDS[tier.name] || TIER_REWARDS['Barn Find']

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-rdc-stone/20 overflow-hidden">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start gap-5">
          <ScoreCircle score={score.score} tier={tier} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display text-lg font-semibold text-black">Provenance Score</h3>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${tier.bgClass} ${tier.textClass}`}>
                <Award size={12} />
                {tier.name}
              </span>
            </div>
            <p className="text-sm text-rdc-charcoal mb-3">{tier.description}</p>

            {/* Progress to next tier */}
            {progress.nextTier && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-rdc-taupe">
                    {progress.pointsUntilNextTier} pts to {progress.nextTier.name}
                  </span>
                  <span className="text-xs text-rdc-taupe">{progress.percentInTier}%</span>
                </div>
                <div className="w-full bg-rdc-cream rounded-full h-2">
                  <div
                    className="bg-rdc-orange h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress.percentInTier}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Breakdown toggle */}
      <button
        onClick={() => setShowBreakdown(!showBreakdown)}
        className="w-full flex items-center justify-between px-6 py-3 bg-rdc-cream/50 border-t border-b border-rdc-stone/10 hover:bg-rdc-cream transition-colors"
      >
        <span className="text-sm font-medium text-rdc-charcoal">Score Breakdown</span>
        {showBreakdown ? <ChevronUp size={16} className="text-rdc-taupe" /> : <ChevronDown size={16} className="text-rdc-taupe" />}
      </button>

      {/* Collapsible breakdown */}
      {showBreakdown && (
        <div className="px-6 py-4 space-y-3 border-b border-rdc-stone/10">
          {breakdown.map((cat) => (
            <CategoryBar key={cat.key} {...cat} />
          ))}
        </div>
      )}

      {/* Next steps (owner only) */}
      {isOwner && actions.length > 0 && (
        <div className="px-6 py-4 border-b border-rdc-stone/10">
          <h4 className="text-sm font-semibold text-black mb-3">Improve Your Score</h4>
          <div className="space-y-2">
            {actions.slice(0, 3).map((action, i) => (
              <Link
                key={i}
                to={action.link}
                className="flex items-center justify-between p-3 bg-rdc-cream/70 rounded-lg hover:bg-rdc-cream transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    action.priority === 'high' ? 'bg-rdc-orange/10 text-rdc-orange' :
                    action.priority === 'medium' ? 'bg-blue-50 text-blue-600' :
                    'bg-gray-100 text-gray-500'
                  }`}>
                    <span className="text-xs font-bold">+{action.points}</span>
                  </div>
                  <span className="text-sm text-rdc-charcoal truncate">{action.action}</span>
                </div>
                <ArrowRight size={14} className="text-rdc-taupe group-hover:text-rdc-orange flex-shrink-0 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Rewards teaser */}
      <div className="px-6 py-4">
        <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-rdc-orange/5 to-amber-50 rounded-lg border border-rdc-orange/10">
          <Gift size={18} className="text-rdc-orange flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-black">
              {tierReward.next ? `Next: ${tierReward.next}` : 'Maximum Rewards Earned'}
            </p>
            <p className="text-xs text-rdc-charcoal mt-0.5">{tierReward.reward}</p>
          </div>
        </div>
        <p className="text-[11px] text-rdc-taupe mt-3">
          Your score and progress are private. Only photos and car details are visible on your public profile.
        </p>
      </div>
    </div>
  )
}
